// src/utils/arrowGeometry.ts
// Géométrie des tracés du PlayCreator : courbe de Bézier quadratique (from → control → to),
// paramétrée par longueur d'arc pour que les découpes, pointes et vagues aient des tailles
// constantes en pixels, quelle que soit la longueur ou la courbure du tracé.

export type Pt = { x: number; y: number };

export type Quad = { p0: Pt; c: Pt; p2: Pt };

export const midpoint = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export const quadPoint = ({ p0, c, p2 }: Quad, t: number): Pt => {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * c.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * c.y + t * t * p2.y,
  };
};

/** Dérivée exacte B'(t). */
export const quadDerivative = ({ p0, c, p2 }: Quad, t: number): Pt => ({
  x: 2 * (1 - t) * (c.x - p0.x) + 2 * t * (p2.x - c.x),
  y: 2 * (1 - t) * (c.y - p0.y) + 2 * t * (p2.y - c.y),
});

/** Tangente unitaire en t (repli sur la corde si la dérivée est nulle). */
export const quadTangent = (q: Quad, t: number): Pt => {
  let d = quadDerivative(q, t);
  let len = Math.hypot(d.x, d.y);
  if (len < 1e-6) {
    d = { x: q.p2.x - q.p0.x, y: q.p2.y - q.p0.y };
    len = Math.hypot(d.x, d.y);
  }
  return len < 1e-6 ? { x: 1, y: 0 } : { x: d.x / len, y: d.y / len };
};

/** Sous-courbe exacte entre t0 et t1 (reste une Bézier quadratique). */
export const subQuad = (q: Quad, t0: number, t1: number): Quad => {
  const p0 = quadPoint(q, t0);
  const d = quadDerivative(q, t0);
  const k = (t1 - t0) / 2;
  return { p0, c: { x: p0.x + d.x * k, y: p0.y + d.y * k }, p2: quadPoint(q, t1) };
};

/** Table longueur d'arc ↔ t, pour convertir une distance en pixels en paramètre t. */
export const arcLengthTable = (q: Quad, samples = 48) => {
  const ts: number[] = [0];
  const ss: number[] = [0];
  let prev = q.p0;
  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    const p = quadPoint(q, t);
    ts.push(t);
    ss.push(ss[i - 1] + Math.hypot(p.x - prev.x, p.y - prev.y));
    prev = p;
  }
  const length = ss[samples];
  const tAt = (s: number): number => {
    if (s <= 0) return 0;
    if (s >= length) return 1;
    let lo = 0;
    let hi = samples;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (ss[mid] < s) lo = mid;
      else hi = mid;
    }
    const f = (s - ss[lo]) / (ss[hi] - ss[lo] || 1);
    return ts[lo] + f * (ts[hi] - ts[lo]);
  };
  return { length, tAt };
};

/** Raccourcit la courbe de `start` px au début et `end` px à la fin (en longueur d'arc). */
export const trimQuad = (q: Quad, start: number, end: number): Quad => {
  if (start <= 0 && end <= 0) return q;
  const { length, tAt } = arcLengthTable(q);
  // on garde toujours un petit bout de tracé visible
  const total = start + end;
  const scale = total > length - 4 ? Math.max(0, length - 4) / (total || 1) : 1;
  return subQuad(q, tAt(start * scale), tAt(length - end * scale));
};

export const quadPathData = ({ p0, c, p2 }: Quad) =>
  `M ${p0.x} ${p0.y} Q ${c.x} ${c.y} ${p2.x} ${p2.y}`;

/** Triangle plein de pointe de flèche dont la pointe est en `tip`, orienté selon `dir`. */
export const arrowHeadPoints = (tip: Pt, dir: Pt, length: number, width: number): number[] => {
  const bx = tip.x - dir.x * length;
  const by = tip.y - dir.y * length;
  const nx = -dir.y * (width / 2);
  const ny = dir.x * (width / 2);
  return [tip.x, tip.y, bx + nx, by + ny, bx - nx, by - ny];
};

/** Segment perpendiculaire à `dir`, centré sur `at` (barre du T de l'écran). */
export const perpendicularBar = (at: Pt, dir: Pt, length: number): number[] => {
  const nx = -dir.y * (length / 2);
  const ny = dir.x * (length / 2);
  return [at.x + nx, at.y + ny, at.x - nx, at.y - ny];
};

/**
 * Tracé ondulé (dribble) le long de la courbe, en polyligne [x0, y0, x1, y1, ...].
 * Longueur d'onde constante en pixels, nombre entier de demi-vagues pour finir à
 * l'amplitude 0, et amplitude atténuée aux deux bouts pour que le tracé parte et
 * arrive bien dans l'axe de la courbe.
 */
export const wavyPoints = (q: Quad, wavelength = 18, amplitude = 6, taper = 10): number[] => {
  const { length, tAt } = arcLengthTable(q);
  if (length < 1) return [q.p0.x, q.p0.y, q.p2.x, q.p2.y];
  const halfWaves = Math.max(2, Math.round((2 * length) / wavelength));
  const steps = Math.max(24, Math.ceil(length / 2));
  const pts: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const s = (i / steps) * length;
    const t = tAt(s);
    const p = quadPoint(q, t);
    const d = quadTangent(q, t);
    const env = Math.min(1, s / taper, (length - s) / taper);
    const off = Math.sin((Math.PI * halfWaves * s) / length) * amplitude * env;
    pts.push(p.x - d.y * off, p.y + d.x * off);
  }
  return pts;
};
